import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { getProjects, getCustomers, createSowDraft, createQuestionnaire, getJobStatus, listQuestionnaires } from '../services/api';
import { toast } from 'sonner';
import { ArrowLeft, Loader2, FileSignature, Upload, HelpCircle, History, ChevronRight } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import JobProgressTracker from '../components/upload/JobProgressTracker';
import FileUploadTab from '../components/upload/FileUploadTab';
import { uploadingStatus } from '../utils/slimZip';

/**
 * SOW Builder input page.
 *
 * Collects a title, free-text brief, optional Halo ticket / email / document
 * files (.pdf/.eml/.msg/.docx/.xlsx/.csv/.txt/.md, or one .zip of the whole
 * pack — the backend unpacks it), an optional pasted raw email, and optional
 * customer / project context, then calls POST /sow/create. Progress is polled
 * off the shared job-status endpoint (~3s); on completion it routes to the SOW
 * editor at /sow-builder/:docId.
 */
const SowBuilderPage = () => {
  const { canUpload } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preselectedProject = searchParams.get('project');

  const [customers, setCustomers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState('');
  const [selectedProject, setSelectedProject] = useState(preselectedProject || '');
  const [title, setTitle] = useState('');
  const [brief, setBrief] = useState('');
  const [pastedEmail, setPastedEmail] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [customerContact, setCustomerContact] = useState('');
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submittingQuestions, setSubmittingQuestions] = useState(false);
  const [pastQuestionnaires, setPastQuestionnaires] = useState([]);

  // Job tracking
  const [jobId, setJobId] = useState(null);
  const [jobStatus, setJobStatus] = useState(null);
  const [jobStartTime, setJobStartTime] = useState(null);
  const [jobElapsedMin, setJobElapsedMin] = useState(0);
  const pollInterval = useRef(null);
  const elapsedTimer = useRef(null);

  const fetchData = useCallback(async () => {
    try {
      const [customersRes, projectsRes] = await Promise.all([
        getCustomers(),
        getProjects(),
      ]);
      setCustomers(customersRes.data);
      setProjects(projectsRes.data);

      if (preselectedProject) {
        const proj = projectsRes.data.find(p => p.id === preselectedProject);
        if (proj) {
          setSelectedCustomer(proj.customer_id);
          setSelectedProject(proj.id);
        }
      }
    } catch (error) {
      toast.error('Failed to load customers and projects');
    } finally {
      setLoading(false);
    }
  }, [preselectedProject]);

  useEffect(() => {
    if (!canUpload) {
      navigate('/documentation');
      return;
    }
    fetchData();
    return () => {
      if (pollInterval.current) clearInterval(pollInterval.current);
      if (elapsedTimer.current) clearInterval(elapsedTimer.current);
    };
  }, [canUpload, navigate, fetchData]);

  const filteredProjects = selectedCustomer
    ? projects.filter(p => p.customer_id === selectedCustomer)
    : projects;

  const startPolling = useCallback((jId) => {
    if (pollInterval.current) clearInterval(pollInterval.current);
    if (elapsedTimer.current) clearInterval(elapsedTimer.current);

    const startTime = Date.now();
    setJobStartTime(startTime);
    setJobElapsedMin(0);

    elapsedTimer.current = setInterval(() => {
      setJobElapsedMin(Math.floor((Date.now() - startTime) / 60000));
    }, 30000);

    pollInterval.current = setInterval(async () => {
      // Poll deadline: a backend restart mid-job can strand it forever. Stop
      // after ~20 min (analysis pass + writer, as DesignBuilderPage) and surface a
      // retry prompt instead of an infinite spinner.
      if (Date.now() - startTime > 20 * 60 * 1000) {
        clearInterval(pollInterval.current);
        clearInterval(elapsedTimer.current);
        pollInterval.current = null;
        elapsedTimer.current = null;
        setJobStatus((s) => (s ? { ...s, status: 'failed', error: 'This page has stopped waiting after 20 minutes. The job may still finish — look for the document under Documentation before starting again.' } : s));
        toast.error('This page has stopped waiting after 20 minutes — the job may still finish.');
        return;
      }
      try {
        const res = await getJobStatus(jId);
        setJobStatus(res.data);

        if (res.data.status === 'complete') {
          clearInterval(pollInterval.current);
          clearInterval(elapsedTimer.current);
          pollInterval.current = null;
          elapsedTimer.current = null;
          const docId = res.data.documentation_id;
          toast.success('SOW draft ready!');
          if (docId) {
            navigate(`/sow-builder/${docId}`);
          }
        } else if (res.data.status === 'failed') {
          clearInterval(pollInterval.current);
          clearInterval(elapsedTimer.current);
          pollInterval.current = null;
          elapsedTimer.current = null;
          toast.error(res.data.error || 'SOW drafting failed');
        }
      } catch (err) {
        console.error('Poll error:', err);
      }
    }, 3000);
  }, [navigate]); // eslint-disable-line react-hooks/exhaustive-deps -- getJobStatus is a stable module import

  // Assemble the shared multipart payload used by both the direct SOW draft and
  // the discovery-questions flow so they stay in lockstep.
  const buildFormData = () => {
    const formData = new FormData();
    formData.append('title', title);
    files.forEach((f) => formData.append('files', f));
    if (brief.trim()) formData.append('brief', brief);
    if (pastedEmail.trim()) formData.append('pasted_email', pastedEmail);
    if (selectedProject) formData.append('project_id', selectedProject);
    if (customerName.trim()) formData.append('customer_name', customerName);
    if (customerContact.trim()) formData.append('customer_contact', customerContact);
    return formData;
  };

  const handleSubmit = async () => {
    if (!title.trim()) {
      toast.error('Please enter a title for the SOW');
      return;
    }

    const formData = buildFormData();

    try {
      setJobStatus({ status: 'uploading', progress: 0, message: 'Uploading inputs...' });
      // Percent, MB sent and time left go onto the job card while it is still uploading
      const response = await createSowDraft(formData, (evt) => {
        setJobStatus((s) => (s?.status === 'uploading' ? uploadingStatus(evt) : s));
      });
      const jId = response.data.job_id;
      setJobId(jId);
      setJobStatus({ status: 'processing', progress: 5, message: 'Drafting scope of work...' });
      startPolling(jId);
    } catch (error) {
      setJobStatus(null);
      setJobId(null);
      toast.error(error.response?.data?.detail || 'Failed to start SOW draft');
    }
  };

  // Generate discovery questions first: same inputs, but route to the
  // questionnaire page instead of drafting the SOW directly.
  const handleGenerateQuestions = async () => {
    if (!title.trim()) {
      toast.error('Please enter a title for the SOW');
      return;
    }
    const formData = buildFormData();
    try {
      setSubmittingQuestions(true);
      const response = await createQuestionnaire(formData);
      const { questionnaire_id, job_id } = response.data;
      navigate(`/sow-builder/questions/${questionnaire_id}`, { state: { job_id } });
    } catch (error) {
      setSubmittingQuestions(false);
      toast.error(error.response?.data?.detail || 'Failed to start discovery questions');
    }
  };

  useEffect(() => {
    // The revisit list: questionnaires are durable working records (answers
    // autosave), so surface the way back to them.
    let cancelled = false;
    (async () => {
      try {
        const res = await listQuestionnaires({ limit: 8 });
        if (!cancelled) setPastQuestionnaires(res.data || []);
      } catch (err) {
        console.error('Failed to load past questionnaires:', err); // non-fatal
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const questionnaireStatusLabel = (q) => {
    if (q.status === 'generating') return 'Generating…';
    if (q.status === 'used') return 'SOW generated';
    if ((q.answered_count || 0) > 0) return `${q.answered_count}/${q.question_count} answered`;
    return `${q.question_count} questions`;
  };

  const resetJob = () => {
    if (pollInterval.current) clearInterval(pollInterval.current);
    if (elapsedTimer.current) clearInterval(elapsedTimer.current);
    setJobId(null);
    setJobStatus(null);
    setFiles([]);
    setTitle('');
    setBrief('');
    setPastedEmail('');
    setCustomerName('');
    setCustomerContact('');
  };

  const isProcessing = jobStatus && !['complete', 'failed'].includes(jobStatus.status);

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-screen">
          <Loader2 className="h-8 w-8 animate-spin text-[#2B8FD8]" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-8 max-w-3xl mx-auto">
        <div className="flex items-center gap-3 mb-8">
          <Button variant="ghost" size="sm" onClick={() => navigate('/design-builder')} data-testid="sow-back-btn">
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900 font-['Plus_Jakarta_Sans']" data-testid="sow-page-title">
              Design Builder · New Design
            </h1>
            <p className="text-slate-500 text-sm">
              Something new — describe it or drop in the Halo ticket / email, and we'll draft the Statement of Work
            </p>
          </div>
        </div>

        {/* Job Status Card */}
        <JobProgressTracker
          jobStatus={jobStatus}
          jobElapsedMin={jobElapsedMin}
          isProcessing={isProcessing}
          onViewDocs={() => jobStatus?.documentation_id && navigate(`/sow-builder/${jobStatus.documentation_id}`)}
          onReset={resetJob}
          workingLabel="Reading the sources and drafting the scope of work..."
          normalTime="5 to 8 minutes"
        />

        {/* Main form - hidden while processing or after completion */}
        {!isProcessing && jobStatus?.status !== 'complete' && (
          <div className="space-y-6">
            {/* Core inputs */}
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle className="text-base font-['Plus_Jakarta_Sans']">What are we scoping?</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Title *</Label>
                  <Input
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="e.g., Cellnet AusPost EDI Integration"
                    data-testid="sow-title-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Brief</Label>
                  <textarea
                    value={brief}
                    onChange={(e) => setBrief(e.target.value)}
                    placeholder="Describe the integration, requirements, systems involved, or paste chat notes / discussion here..."
                    rows={8}
                    className="flex w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm ring-offset-white placeholder:text-slate-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2B8FD8] focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 resize-y"
                    data-testid="sow-brief-input"
                  />
                  <p className="text-xs text-slate-500">
                    The more context you give here, the sharper the draft. Free-form notes are fine.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Source files */}
            <div>
              <h3 className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-2">
                <Upload className="h-4 w-4" />
                Halo ticket / email files <span className="text-slate-400 font-normal">(optional)</span>
              </h3>
              <FileUploadTab
                file={files}
                setFile={setFiles}
                onUpload={handleSubmit}
                disabled={!title.trim()}
                multiple
                accept=".pdf,.eml,.msg,.docx,.xlsx,.csv,.txt,.md,.zip"
                hint="Halo tickets, emails, Word / Excel / CSV exports — PDF, EML, MSG, DOCX, XLSX, CSV, TXT, MD, or one ZIP of the whole pack"
              />
            </div>

            {/* Pasted email */}
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle className="text-base font-['Plus_Jakarta_Sans']">Paste raw email</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <Label>Email content <span className="text-slate-400 font-normal">(optional)</span></Label>
                  <textarea
                    value={pastedEmail}
                    onChange={(e) => setPastedEmail(e.target.value)}
                    placeholder="Paste the raw email thread here if you don't have a file to upload..."
                    rows={6}
                    className="flex w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm ring-offset-white placeholder:text-slate-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2B8FD8] focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 resize-y"
                    data-testid="sow-pasted-email-input"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Customer / project context */}
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle className="text-base font-['Plus_Jakarta_Sans']">
                  Customer & project <span className="text-slate-400 font-normal text-sm">(optional)</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Customer</Label>
                    <Select
                      value={selectedCustomer}
                      onValueChange={(v) => {
                        setSelectedCustomer(v);
                        setSelectedProject('');
                        const c = customers.find(c => c.id === v);
                        if (c && !customerName.trim()) setCustomerName(c.name);
                      }}
                    >
                      <SelectTrigger data-testid="sow-customer-select">
                        <SelectValue placeholder="Select customer" />
                      </SelectTrigger>
                      <SelectContent>
                        {customers.map(c => (
                          <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Project</Label>
                    <Select value={selectedProject} onValueChange={setSelectedProject}>
                      <SelectTrigger data-testid="sow-project-select">
                        <SelectValue placeholder="Select project" />
                      </SelectTrigger>
                      <SelectContent>
                        {filteredProjects.map(p => (
                          <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Customer name (for the SOW header)</Label>
                    <Input
                      value={customerName}
                      onChange={(e) => setCustomerName(e.target.value)}
                      placeholder="e.g., Cygnett Pty Ltd"
                      data-testid="sow-customer-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Customer contact</Label>
                    <Input
                      value={customerContact}
                      onChange={(e) => setCustomerContact(e.target.value)}
                      placeholder="e.g., Jane Doe, jane@customer.com"
                      data-testid="sow-customer-contact-input"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Primary submit */}
            <Button
              className="w-full bg-[#2B8FD8] hover:bg-[#2075b3] h-12 text-base"
              onClick={handleSubmit}
              disabled={!title.trim() || submittingQuestions}
              data-testid="sow-generate-btn"
            >
              <FileSignature className="h-5 w-5 mr-2" />
              Generate SOW Draft
            </Button>

            {/* Secondary: build discovery questions first */}
            <Button
              variant="outline"
              className="w-full h-11 text-sm border-[#2B8FD8]/40 text-[#2B8FD8] hover:bg-blue-50 hover:text-[#2075b3]"
              onClick={handleGenerateQuestions}
              disabled={!title.trim() || submittingQuestions}
              data-testid="sow-generate-questions-btn"
            >
              {submittingQuestions ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Preparing questions...</>
              ) : (
                <><HelpCircle className="h-4 w-4 mr-2" /> Generate discovery questions first</>
              )}
            </Button>
            <p className="text-xs text-center text-slate-500 -mt-2">
              Not sure of every detail? Generate the questions worth asking the customer first, capture their answers, then draft the SOW.
            </p>

            {pastQuestionnaires.length > 0 && (
              <Card className="border-slate-200" data-testid="sow-questionnaire-history">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base font-['Plus_Jakarta_Sans'] flex items-center gap-2">
                    <History className="h-4 w-4 text-slate-400" /> Recent questionnaires
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-0 divide-y divide-slate-100">
                  {pastQuestionnaires.map((q) => (
                    <button
                      key={q.id}
                      type="button"
                      onClick={() => navigate(`/sow-builder/questions/${q.id}`)}
                      className="w-full flex items-center justify-between gap-3 py-2.5 text-left hover:bg-slate-50 rounded-md px-2 -mx-2"
                      data-testid={`sow-questionnaire-row-${q.id}`}
                    >
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-slate-800 truncate">{q.title}</p>
                        <p className="text-xs text-slate-500">
                          {questionnaireStatusLabel(q)}
                          {q.updated_at ? ` · ${new Date(q.updated_at).toLocaleDateString()}` : ''}
                        </p>
                      </div>
                      <ChevronRight className="h-4 w-4 shrink-0 text-slate-300" />
                    </button>
                  ))}
                </CardContent>
              </Card>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default SowBuilderPage;
